import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { NavLink } from 'react-router-dom'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { CoffeeWebWhiteLogoMask } from 'Assets/Icons'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'

const FreeMenubar = ({ guestUserMenus }) => {
  const location = useLocation()
  const { isLoading } = useContext(CoffeeWebContext)
  const dropdownRef = useRef(null)
  const params = new URLSearchParams(window.location.search)
  const tab = params.get('tab')
  const view = params.get('view')

  const [activeTab, setActiveTab] = useState(tab || 'business') // Default to 'business'
  const [isExploreOpen, setIsExploreOpen] = useState(false)

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExploreOpen(false)
      }
    }

    document.addEventListener('click', handleOutsideClick)

    return () => document.removeEventListener('click', handleOutsideClick)
  }, [])

  useEffect(() => {
    setIsExploreOpen(false)
  }, [location.pathname])

  // Helper function to determine which tab a route belongs to
  const getTabForCurrentRoute = () => {
    // if (tab && tab === 'general') return 'general'

    if (tab === 'general' || tab === 'business') {
      return tab
    }

    const currentPath = location.pathname
    const params = new URLSearchParams(window.location.search)
    const view = params.get('view')

    if (!guestUserMenus?.generalMenu || !guestUserMenus?.businessMenu) return 'business'

    const businessMenus = guestUserMenus?.businessMenu || []
    const generalMenus = guestUserMenus?.generalMenu || []

    // Handle coffeeweb route with view parameter
    if (currentPath === ROUTE_STRINGS.guestCoffeeWeb || currentPath.includes('/coffeeweb')) {
      if (view) {
        // Check if the view belongs to business or general menus
        const normalizedView = view.toLowerCase()
        const isInBusiness = businessMenus.some((menu) => {
          const normalizedMenuUrl = menu.menuUrl?.replace(/^\//, '')?.toLowerCase()

          return (
            normalizedMenuUrl === normalizedView ||
            (normalizedMenuUrl === 'coffeeweb' &&
              businessMenus.some((m) => {
                const subMenuUrl = m.menuUrl?.replace(/^\//, '')?.toLowerCase()

                return subMenuUrl === normalizedView
              }))
          )
        })
        const isInGeneral = generalMenus.some((menu) => {
          const normalizedMenuUrl = menu.menuUrl?.replace(/^\//, '')?.toLowerCase()

          return (
            normalizedMenuUrl === normalizedView ||
            (normalizedMenuUrl === 'coffeeweb' &&
              generalMenus.some((m) => {
                const subMenuUrl = m.menuUrl?.replace(/^\//, '')?.toLowerCase()

                return subMenuUrl === normalizedView
              }))
          )
        })

        if (isInBusiness) return 'business'
        if (isInGeneral) return 'general'
      }
      // Default: if on coffeeweb without view, check tab param or default to general

      return tab === 'business' ? 'business' : 'general'
    }

    // Handle CoffeeWebAi route
    if (currentPath === ROUTE_STRINGS.guestCoffeeWebAi || currentPath.includes('/coffeewebAi')) {
      const isInBusiness = businessMenus.some((menu) => {
        const normalizedMenuUrl = menu.menuUrl?.replace(/^\//, '')?.toLowerCase()

        return normalizedMenuUrl === 'coffeewebai'
      })
      const isInGeneral = generalMenus.some((menu) => {
        const normalizedMenuUrl = menu.menuUrl?.replace(/^\//, '')?.toLowerCase()

        return normalizedMenuUrl === 'coffeewebai'
      })

      if (isInBusiness) return 'business'
      if (isInGeneral) return 'general'
    }

    // Check if current route is in business menus
    const isInBusiness = businessMenus.some((menu) => {
      // eslint-disable-next-line
      const menuUrl = menu.menuUrl === 'CoffeeQuotes' ? ROUTE_STRINGS.guestCoffeeQuotes : '/' + menu.menuUrl

      return currentPath.includes(menuUrl) || currentPath === menuUrl
    })

    // Check if current route is in general menus
    const isInGeneral = generalMenus.some((menu) => {
      // eslint-disable-next-line
      const menuUrl = '/' + menu.menuUrl

      return currentPath.includes(menuUrl) || currentPath === menuUrl
    })

    if (isInBusiness && isInGeneral) return activeTab
    if (isInBusiness) return 'business'
    if (isInGeneral) return 'general'

    // Default to business if route not found in either
    return 'business'
  }

  // Set active tab based on current route on mount (handles refresh)
  useEffect(() => {
    const tabForRoute = getTabForCurrentRoute()

    setActiveTab(tabForRoute)
  }, [location.pathname, location.search])

  /* eslint-disable prefer-template */
  const transformMenuUrl = (url) => '/' + url

  const coffeewebViewMenus = ['coffeecast', 'coffee-games', 'coffee-community', 'coffeecart', 'coffee-post', 'cafes']

  // Helper function to get the correct navigation URL for a menu
  const getMenuNavigationUrl = (menu) => {
    // Normalize the menu URL
    const normalizedMenuUrl = menu.menuUrl?.replace(/^\//, '')?.toLowerCase()

    const menuGroup = activeTab === 'business' ? guestUserMenus.businessMenu : guestUserMenus.generalMenu
    const coffeewebMenuId = menuGroup?.find((el) => el.menuName?.toLowerCase() === 'coffeeweb')?.id

    // Check if this menu should use coffeeweb route with view params
    if (menu.parentId === coffeewebMenuId) {
      return `${ROUTE_STRINGS.guestCoffeeWeb}?view=${normalizedMenuUrl}&tab=${activeTab}`
    }

    if (menu.parentId !== coffeewebMenuId) {
      if (normalizedMenuUrl === 'coffeewebai' || normalizedMenuUrl === 'coffeewebai') {
        return `${ROUTE_STRINGS.guestCoffeeWebAi}?tab=${activeTab}`
      }
      if (normalizedMenuUrl === 'coffeepedia') {
        return `${ROUTE_STRINGS.guestCoffeePedia}?tab=${activeTab}`
      }
      if (normalizedMenuUrl === 'coffee-apps') {
        return `${ROUTE_STRINGS.guestCoffeeApps}?tab=${activeTab}`
      }
    }

    if (normalizedMenuUrl === 'coffeeweb') {
      if (normalizedMenuUrl === 'coffeewebai' || normalizedMenuUrl === 'coffeewebai') {
        return `${ROUTE_STRINGS.guestCoffeeWebAi}?tab=${activeTab}`
      }

      return `${ROUTE_STRINGS.guestCoffeeWeb}?view=${ROUTE_STRINGS.coffeePost}&tab=${activeTab}`
    }

    // For all other menus (like CoffeeQuotes), use normal route transformation
    return menu.menuUrl === 'CoffeeQuotes' ? ROUTE_STRINGS.guestCoffeeQuotes : transformMenuUrl(menu.menuUrl)
  }

  const getFilteredMenus = () => {
    if (!guestUserMenus) return []

    if (activeTab === 'business') {
      return guestUserMenus?.businessMenu || []
    }

    return guestUserMenus?.generalMenu || []
  }

  const filteredMenus = getFilteredMenus()

  // Kept it for future reference
  // Handle tab switching with navigation to default routes
  // const handleTabSwitch = (tab) => {
  //   if (tab === activeTab) return // Don't do anything if clicking the same tab

  //   setActiveTab(tab)

  //   // Navigate to default route for the selected tab
  //   if (tab === 'business') {
  //     // Navigate to Coffee Quotes (first business menu item)
  //     const businessMenus = guestUserMenus?.businessMenu || []
  //     const coffeeQuotesMenu = businessMenus.find((menu) => menu.menuUrl === 'CoffeeQuotes')

  //     if (coffeeQuotesMenu) {
  //       history.push(ROUTE_STRINGS.guestCoffeeQuotes)
  //     } else if (businessMenus.length > 0) {
  //       // Fallback to first menu item
  //       const firstMenu = businessMenus[0]

  //       history.push(firstMenu.menuUrl === 'CoffeeQuotes' ? ROUTE_STRINGS.guestCoffeeQuotes : transformMenuUrl(firstMenu.menuUrl))
  //     }
  //   } else if (tab === 'general') {
  //     // Navigate to first general menu item
  //     const generalMenus = guestUserMenus?.generalMenu || []

  //     if (generalMenus.length > 0) {
  //       history.push(`${ROUTE_STRINGS.guestCoffeeWeb}?view=${ROUTE_STRINGS.coffeePost}&tab=general`)
  //     } else {
  //       // Fallback if no general menus
  //       history.push(`${ROUTE_STRINGS.guestCoffeeWeb}?view=${ROUTE_STRINGS.coffeePost}&tab=general`)
  //     }
  //   }
  // }

  const parentMenuWithSubmenus = guestUserMenus?.businessMenu?.find((menu) => menu.subMenuModels?.length > 0 && menu?.menuUrl === 'menu')

  return (
    <>
      <div className={`post_header ${isLoading ? 'is-loading' : ''}`} data-testid="menu-main-container">
        <div className="" id="navbar-main" data-testid="navbar-main-container">
          <div>
            <div aria-controls="basic-navbar-nav" />
            <div id="basic-navbar-nav" data-testid="basic-navbar-container">
              <ul className="navbar-nav" data-testid="navbar-ul-container">
                {filteredMenus !== null &&
                  filteredMenus.length > 0 &&
                  filteredMenus.map((ele, index) => (
                    <div key={index}>
                      {ele.isFreeMenuWebDisplayEnable === true && ele.isLaunch === true && ele.menuUrl !== null && (
                        <li className="nav-item1 menucolor1" id="navitem" data-testid="navbar-li-container">
                          <NavLink
                            activeClassName="active_nav-link1"
                            exact
                            to={getMenuNavigationUrl(ele)}
                            className="nav-link1 nav-link-ltr"
                            isActive={(match, location) => {
                              const normalizedMenuUrl = ele?.menuUrl?.replace(/^\//, '')?.toLowerCase()

                              // For coffeeweb view menus, check if we're on coffeeweb route with matching view param
                              if (coffeewebViewMenus.includes(normalizedMenuUrl)) {
                                const params = new URLSearchParams(location.search)
                                const view = params.get('view')

                                return location.pathname === ROUTE_STRINGS.guestCoffeeWeb && view?.toLowerCase() === normalizedMenuUrl
                              }

                              // For CoffeeWebAi, check the specific route
                              if (normalizedMenuUrl === 'coffeewebai') {
                                return location.pathname === ROUTE_STRINGS.guestCoffeeWebAi
                              }

                              // For other menus, use default NavLink active matching
                              return match !== null
                            }}
                          >
                            <div className="menu-name-container" data-testid="menu-name-container">
                              {ele.menuIcon && <img src={ele.menuUrl === 'aboutUs' && !location.pathname?.includes(ROUTE_STRINGS.aboutUs) ? CoffeeWebWhiteLogoMask : ele?.menuIcon} alt="menu-image" className="menu-icon" />}
                              {ele.menuName}
                            </div>
                          </NavLink>
                        </li>
                      )}
                    </div>
                  ))}
                {/* Explore Dropdown Menu */}
                {activeTab === 'business' && parentMenuWithSubmenus?.menuName && (
                  <li
                    className="nav-item1 menucolor1 dropdown-li-explore"
                    ref={dropdownRef}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsExploreOpen((prev) => !prev)
                    }}
                  >
                    <div className="nav-link1 dropdown-toggle" data-testid="explore-menu">
                      {parentMenuWithSubmenus?.menuName}
                      <i className="pi pi-bars"></i>
                    </div>
                    {isExploreOpen && (
                      <ul className="dropdown-menu-ul" data-testid="dropdown-menus">
                        {parentMenuWithSubmenus?.subMenuModels?.map((menu) => (
                          <li key={menu.id}>
                            {/* <NavLink exact to={transformMenuUrl(menu.menuUrl)} className="dropdown-item-navLink" onClick={() => setIsExploreOpen(false)}> */}
                            <NavLink exact to={transformMenuUrl(menu.menuUrl)} className={`${view === 'coffee-post' && 'business-active-tab-navLink'} dropdown-item-navLink`} onClick={() => setIsExploreOpen(false)}>
                              <div className={`${view === 'coffee-post' && 'business-active-tab-content'} menu-dropdown-content`}>
                                {menu.menuIcon && <img src={menu.menuIcon} alt="menu-image" className="menu-icon" />}
                                {menu.menuName}
                              </div>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* // Kept this code for future reference in case we want to revert it back */}
      {/* <div className={`business-general-container-menubar ${isLoading ? 'on-loading-z-index-business-general-container' : ''} ${userDetails?.id ? '' : 'guest-user-business-container'} `}>
        <div
          className={`business-container ${activeTab === 'business' ? 'active-tab' : ''}`}
          onClick={() => handleTabSwitch('business')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleTabSwitch('business')
            }
          }}
        >
          BUSINESS
        </div>
        <div
          className={`general-container ${activeTab === 'general' ? 'active-tab' : ''}`}
          onClick={() => handleTabSwitch('general')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleTabSwitch('general')
            }
          }}
        >
          GENERAL
        </div>
      </div> */}
    </>
  )
}

export default FreeMenubar
